import { createContext, useContext, useState, useEffect } from 'react';

import type { WebsiteSection } from '@/types/website';
import {WEBSITE_SECTIONS } from '@/config/sections';

interface WebsiteContextType {
  sections: WebsiteSection[];
  loading: boolean;
  getSection: (sectionId: string) => WebsiteSection | undefined;
  isSectionVisible: (sectionId: string) => boolean;
}

const WebsiteContext = createContext<WebsiteContextType | undefined>(undefined);

export function WebsiteProvider({ children }: { children: React.ReactNode }) {
  const [sections, setSections] = useState<WebsiteSection[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Use the local configuration directly
    setSections(WEBSITE_SECTIONS);
    setLoading(false);
  }, []);

  const getSection = (sectionId: string) => {
    return sections.find(s => s.id === sectionId);
  };

  const isSectionVisible = (sectionId: string) => {
    const section = getSection(sectionId);
    return section?.is_active ?? false;
  };

  const value = {
    sections,
    loading,
    getSection,
    isSectionVisible,
  };

  return (
    <WebsiteContext.Provider value={value}>
      {children}
    </WebsiteContext.Provider>
  );
}

export function useWebsite() {
  const context = useContext(WebsiteContext);
  if (context === undefined) {
    throw new Error('useWebsite must be used within a WebsiteProvider');
  }
  return context;
} 